import React, { useEffect, useState } from 'react';
import {
  WxbInput,
  WxbModal,
  WxbSelect,
  WxbTextarea,
} from '../wxb-ui';
import {
  FABRICATION_TYPE_LABELS,
  RESOURCE_KIND_LABELS,
} from './equipmentStateCatalogModel';
import type {
  EquipmentStateApplicableResourceKind,
  EquipmentStateClass,
  EquipmentStateClassDraft,
  EquipmentStateFabricationType,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  open: boolean;
  initialClass: EquipmentStateClass | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (draft: EquipmentStateClassDraft) => Promise<void>;
}

const EMPTY_DRAFT: EquipmentStateClassDraft = {
  code: '',
  name: '',
  fabricationType: 'OTHER',
  applicableResourceKinds: ['EQUIPMENT'],
  note: null,
};

const EquipmentClassModal: React.FC<Props> = ({ open, initialClass, saving, onCancel, onSave }) => {
  const [draft, setDraft] = useState<EquipmentStateClassDraft>(EMPTY_DRAFT);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setDraft(initialClass ? {
      code: initialClass.code,
      name: initialClass.name,
      fabricationType: initialClass.fabricationType,
      applicableResourceKinds: [...initialClass.applicableResourceKinds],
      note: initialClass.note,
    } : EMPTY_DRAFT);
  }, [initialClass, open]);

  const codeError = submitted && !draft.code.trim() ? '请输入设备类编码。' : undefined;
  const nameError = submitted && !draft.name.trim() ? '请输入设备类名称。' : undefined;
  const kindsError = submitted && draft.applicableResourceKinds.length === 0 ? '至少选择一种适用资源类型。' : undefined;

  const submit = async () => {
    setSubmitted(true);
    if (!draft.code.trim() || !draft.name.trim() || draft.applicableResourceKinds.length === 0) return;
    await onSave({
      ...draft,
      code: draft.code.trim().toUpperCase(),
      name: draft.name.trim(),
      note: draft.note?.trim() || null,
    });
  };

  return (
    <WxbModal
      open={open}
      title={initialClass ? '编辑设备类别' : '新建设备类别'}
      okText={initialClass ? '保存类别' : '创建类别'}
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={submit}
      destroyOnClose
      width={620}
    >
      <div className="esc-form-grid">
        <WxbInput
          label="类别编码"
          value={draft.code}
          disabled={Boolean(initialClass)}
          error={codeError}
          placeholder="例如 SS_PREP_TANK"
          helpText={initialClass ? '稳定编码创建后不可修改。' : '建议使用大写字母、数字与下划线。'}
          onChange={(event) => setDraft((current) => ({ ...current, code: event.target.value }))}
        />
        <WxbInput
          label="类别名称"
          value={draft.name}
          error={nameError}
          placeholder="例如 不锈钢配液罐"
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />
        <WxbSelect
          label="制造形态"
          value={draft.fabricationType}
          options={Object.entries(FABRICATION_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          onChange={(value) => setDraft((current) => ({
            ...current,
            fabricationType: value as EquipmentStateFabricationType,
          }))}
        />
        <WxbSelect
          label="适用资源类型"
          mode="multiple"
          value={draft.applicableResourceKinds}
          error={kindsError}
          options={Object.entries(RESOURCE_KIND_LABELS).map(([value, label]) => ({ value, label }))}
          placeholder="选择可绑定的资源类型"
          onChange={(values) => setDraft((current) => ({
            ...current,
            applicableResourceKinds: values as EquipmentStateApplicableResourceKind[],
          }))}
        />
        <div className="esc-form-span">
          <WxbTextarea
            label="说明"
            value={draft.note ?? ''}
            placeholder="说明该类资源的状态管理边界。"
            onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
          />
        </div>
      </div>
    </WxbModal>
  );
};

export default EquipmentClassModal;
